/**
 * Keys in the session storage that we set on a browser.
 *
 * Session storage is scoped per-tab and cleared when the tab is closed. Use it for state that
 * should survive a full page refresh but should not leak across tabs or browser sessions.
 */
const sessionStorageKeys = [
  'mwp-ai-agent-messages', // Persisted AI Agent chat history for the current tab
  'mwp-ai-agent-session-id', // Persisted AI Agent backend session id for the current tab
] as const;

type SessionStorageKey = (typeof sessionStorageKeys)[number];

/**
 * Sets the session storage value for a given key. Type safe.
 *
 * Swallows browser errors (e.g. `QuotaExceededError` when storage is full,
 * `SecurityError` when storage access is denied) and logs a warning so callers
 * can treat this as fire-and-forget.
 */
export function setSessionStorageItem(key: SessionStorageKey, value: string): void {
  try {
    if (typeof sessionStorage === 'undefined') return;

    // eslint-disable-next-line no-restricted-syntax
    sessionStorage.setItem(key, value);
  } catch (error) {
    console.warn(`Failed to set session storage item "${key}"`, error);
  }
}

/**
 * Retrieves the session storage value for a given key. Type safe.
 *
 * Returns `null` if storage is unavailable or the read throws (e.g. a
 * `SecurityError` in environments where storage access is blocked).
 */
export function getSessionStorageItem(key: SessionStorageKey): string | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;

    // eslint-disable-next-line no-restricted-syntax
    return sessionStorage.getItem(key) ?? null;
  } catch (error) {
    console.warn(`Failed to read session storage item "${key}"`, error);
    return null;
  }
}

/**
 * Removes an item from session storage for a given key. Type safe.
 *
 * Swallows browser errors and logs a warning so callers can treat this as
 * fire-and-forget.
 */
export function removeSessionStorageItem(key: SessionStorageKey): void {
  try {
    if (typeof sessionStorage === 'undefined') return;

    // eslint-disable-next-line no-restricted-syntax
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to remove session storage item "${key}"`, error);
  }
}
